import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { Check } from 'lucide-react'
import { type ComponentPropsWithoutRef, forwardRef } from 'react'

import { cn } from '@/lib/utils'

/**
 * Dropdown menu — Radix primitives, shadcn-style chrome.
 *
 * Used for the per-row "⋮" actions (Modifier / Supprimer) on
 * planning rows and, later, on missions / vehicles tables. Radix
 * handles keyboard navigation, focus return, and portal rendering
 * out of the box.
 *
 * Re-exports
 * ----------
 *   - `DropdownMenu`         (Root)
 *   - `DropdownMenuTrigger`
 *   - `DropdownMenuContent`  styled
 *   - `DropdownMenuItem`     styled
 *   - `DropdownMenuLabel`    styled
 *   - `DropdownMenuSeparator` styled
 *   - `DropdownMenuCheckboxItem` styled
 */

export const DropdownMenu = DropdownMenuPrimitive.Root
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal

export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(function DropdownMenuContent({ className, sideOffset = 6, ...props }, ref) {
  return (
    <DropdownMenuPortal>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          'z-50 min-w-[12rem] overflow-hidden',
          'rounded-md border border-surface-border bg-surface shadow-lg',
          'p-1',
          'animate-in fade-in-0 zoom-in-95',
          className,
        )}
        {...props}
      />
    </DropdownMenuPortal>
  )
})

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    danger?: boolean
  }
>(function DropdownMenuItem({ className, danger = false, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(
        'flex items-center gap-2',
        'px-2 py-2 rounded-sm text-sm cursor-pointer outline-none',
        'data-[highlighted]:bg-surface-hover',
        'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
        danger
          ? 'text-danger data-[highlighted]:bg-danger-soft'
          : 'text-text',
        className,
      )}
      {...props}
    />
  )
})

export const DropdownMenuLabel = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label>
>(function DropdownMenuLabel({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Label
      ref={ref}
      className={cn('px-2 py-1.5 text-xs uppercase tracking-wide text-text-muted', className)}
      {...props}
    />
  )
})

export const DropdownMenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(function DropdownMenuSeparator({ className, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.Separator
      ref={ref}
      className={cn('my-1 h-px bg-surface-border', className)}
      {...props}
    />
  )
})

export const DropdownMenuCheckboxItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={cn(
        'flex items-center gap-2',
        'pl-7 pr-2 py-2 rounded-sm text-sm cursor-pointer outline-none relative',
        'data-[highlighted]:bg-surface-hover text-text',
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 inline-flex h-4 w-4 items-center justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <Check size={14} aria-hidden="true" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
})
